import { ClientProfileRelationshipType } from '@/types/schema';

import { INDETERMINATE_GENERATION_SENTINEL } from '../constants/relationships';
import {
  FamilyRelationships,
  type FamilyRelationshipType,
} from './relationshipUtils';

export type RelationshipVariant = 'spouse' | 'children' | 'parents';

export interface LinkableOption {
  id: string;
  displayName: string;
  [key: string]: unknown;
}

export interface FamilyTreePerson {
  id: string;
  clientProfile?: {
    id: string;
  } | null;
  generation: number;
}

export interface LinkableOptionWithDisabled extends LinkableOption {
  disabled: boolean;
}

/**
 * Get the set of relationship types that should be excluded when adding
 * a relationship of a given variant
 */
function getExcludedRelationshipTypes(
  variant: RelationshipVariant
): FamilyRelationshipType[] {
  switch (variant) {
    case 'spouse':
      // For spouses: exclude children, parents, and existing spouses
      return [
        ClientProfileRelationshipType.Child,
        ClientProfileRelationshipType.StepChild,
        ClientProfileRelationshipType.Parent,
        ClientProfileRelationshipType.StepParent,
        ClientProfileRelationshipType.Spouse,
        ClientProfileRelationshipType.ExSpouse,
      ];
    case 'children':
      // For children: exclude parents, spouses
      return [
        ClientProfileRelationshipType.Parent,
        ClientProfileRelationshipType.StepParent,
        ClientProfileRelationshipType.Spouse,
        ClientProfileRelationshipType.ExSpouse,
        ClientProfileRelationshipType.Child,
        ClientProfileRelationshipType.StepChild,
      ];
    case 'parents':
      // For parents: exclude spouses, children
      return [
        ClientProfileRelationshipType.Spouse,
        ClientProfileRelationshipType.ExSpouse,
        ClientProfileRelationshipType.Child,
        ClientProfileRelationshipType.StepChild,
        ClientProfileRelationshipType.Parent,
        ClientProfileRelationshipType.StepParent,
      ];
  }
}

/**
 * Filters linkable options and marks which ones should be disabled based on
 * existing relationships and generation requirements.
 *
 * Rules:
 * - The selected person cannot be their own spouse/child/parent
 * - For spouses: exclude existing children, parents, and spouses
 * - For children: exclude existing parents, spouses, and children
 * - For parents: exclude existing spouses, children, and parents
 * - Generation requirements (if both people have valid generations):
 *   - Spouses: must be same generation (diff = 0)
 *   - Children: must be +1 generation (one generation below)
 *   - Parents: must be -1 generation (one generation above)
 * - People without a generation (undefined/null) are not disabled based on generation
 * - People with generation -99 (sentinel for "couldn't calculate") are not disabled based on generation
 *
 * @param options - All available client profiles that could be linked
 * @param selectedPersonId - The ID of the currently selected person
 * @param variant - The type of relationship being added (spouse, children, parents)
 * @param relationships - The existing relationships of the selected person
 * @param familyTreePeople - Array of people with generation data from the family tree
 * @returns Array of options with disabled flag indicating if they can be selected
 */
export function filterLinkableOptions(
  options: LinkableOption[],
  selectedPersonId: string | null,
  variant: RelationshipVariant,
  relationships: FamilyRelationships,
  familyTreePeople: FamilyTreePerson[] = []
): LinkableOptionWithDisabled[] {
  // Get IDs of people who should be disabled based on variant
  const disabledIds = new Set<string>();

  // Always disable the selected person (can't be their own spouse/child/parent)
  if (selectedPersonId) {
    disabledIds.add(selectedPersonId);
  }

  // Get the relationship types to exclude for this variant
  const excludedTypes = getExcludedRelationshipTypes(variant);

  // Add all people who have excluded relationship types to the disabled set
  excludedTypes.forEach((relationshipType) => {
    relationships[relationshipType]?.forEach((person) => {
      disabledIds.add(person.id);
    });
  });

  // Add generation-based filtering
  // Enforce specific generation requirements based on relationship type:
  // - Spouses must be same generation (diff = 0)
  // - Children must be +1 generation (one generation below)
  // - Parents must be -1 generation (one generation above)
  // Note: -99 is a sentinel value indicating the generation could not be calculated
  const selectedPersonGeneration = familyTreePeople.find(
    (p) => p.clientProfile?.id === selectedPersonId
  )?.generation;

  // Only apply generation filtering if the selected person has a valid generation
  // (not null/undefined and not -99)
  if (
    selectedPersonGeneration != null &&
    selectedPersonGeneration !== INDETERMINATE_GENERATION_SENTINEL
  ) {
    options.forEach((option) => {
      // Look up the option's generation from familyTreePeople
      const optionGeneration = familyTreePeople.find(
        (p) => p.clientProfile?.id === option.id
      )?.generation;

      // Only apply filtering if the option has a defined and valid generation
      // (not null/undefined and not -99)
      if (
        optionGeneration != null &&
        optionGeneration !== INDETERMINATE_GENERATION_SENTINEL
      ) {
        const generationDiff = optionGeneration - selectedPersonGeneration;

        // Disable based on variant-specific generation rules
        let shouldDisable = false;

        switch (variant) {
          case 'spouse':
            // Spouses must be same generation
            shouldDisable = generationDiff !== 0;
            break;
          case 'children':
            // Children must be +1 generation (one below)
            shouldDisable = generationDiff !== 1;
            break;
          case 'parents':
            // Parents must be -1 generation (one above)
            shouldDisable = generationDiff !== -1;
            break;
        }

        if (shouldDisable) {
          disabledIds.add(option.id);
        }
      }
      // If option has no generation or generation is -99, don't disable based on generation
    });
  }

  // Filter out the selected person and mark disabled options
  return options
    .filter((option) => option.id !== selectedPersonId)
    .map((option) => ({
      ...option,
      disabled: disabledIds.has(option.id),
    }));
}
